import { useField } from '@framework/form';
import React, { useCallback, useEffect } from 'react';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem';
import { IWidgetComponentProps } from '../../../IWidgetType';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps';
import {
  IRepeatControllerWidget,
  RepeatControllerComponentType,
} from './IRepeatControllerWidget';
import { RepeatControllerInput } from './components/RepeatControllerInput';
import { useRepeatControllerConfirmModal } from './components/useRepeatControllerConfirmModal';
import { useRepeatableListEditor } from './useRepeatableListEditor';

export interface IRepeatControllerWidgetComponentProps
  extends IWidgetComponentProps<IRepeatControllerWidget> {}

export function RepeatControllerWidgetComponent(
  props: IRepeatControllerWidgetComponentProps,
) {
  const { widget } = props;
  const options = getWidgetProps(props);

  const inputField = useField<number>(options.name);
  const listEditor = useRepeatableListEditor(
    options.name,
    widget.listReference,
  );

  const { handleModal, contextHolder } = useRepeatControllerConfirmModal();

  /**
   * Aggiorno la lunghezza della lista in caso di modifica al campo
   */
  const handleChange = useCallback(
    async (
      value: number | null,
      changeControllerFieldValue: (...input: any) => void,
    ): Promise<void> => {
      if (value === null) {
        return;
      }

      // Non faccio modifiche in caso di valori fuori range
      if (
        listEditor.itemRangeRuleValues.max != null &&
        value > listEditor.itemRangeRuleValues.max
      ) {
        return;
      }

      // Non faccio modifiche in caso di valori fuori range
      if (
        listEditor.itemRangeRuleValues.min != null &&
        value < listEditor.itemRangeRuleValues.min
      ) {
        return;
      }

      const actualValue = listEditor.field.getValue();
      const actualLength = actualValue?.length ?? 0;

      // Aggiungo elementi se richiesto
      if (actualLength < value) {
        const newValue = [...(actualValue ?? [])];
        for (let i = actualLength; i < value; i++) {
          newValue.push(listEditor.getDefaultValue() ?? {});
        }
        listEditor.field.setValue(newValue);
      }

      // Rimuovo elementi dal fondo se richiesto
      // Mostro una modal all'utente per la conferma della cancellazione
      if (actualLength > value) {
        const updateListValue = () => {
          const newValue = [...(actualValue ?? [])];
          newValue.splice(value);
          listEditor.field.setValue(newValue);
        };

        // Se è richiesta la conferma, mostro la modal
        if (widget.confirmReduce) {
          // Passo dinamicamente le funzioni di OK e Cancel alla modal
          await handleModal({
            onOk: () => {
              updateListValue();
            },
            onCancel: () => {
              changeControllerFieldValue(actualLength);
            },
          });
        } else {
          updateListValue();
        }
      }
    },
    [listEditor, handleModal, widget.confirmReduce],
  );

  /** Aggiorno il campo quando viene modificata la lunghezza della lista */
  useEffect(() => {
    const listValue = listEditor.values;
    const inputFieldValue = inputField.getValue();

    // Se non ci sono modifiche, non faccio nulla per evitare di aggiornare inutilmente lo stato
    if (listValue && listValue.length === inputFieldValue) {
      return;
    }

    // Se non ci sono più elementi (e il campo non è stato annullato/undefined), lo imposto a zero
    if ((!listValue || listValue.length === 0) && inputFieldValue != null) {
      inputField.setValue(0);
      return;
    }

    if ((!listValue || listValue.length === 0) && inputFieldValue == null) {
      return;
    }

    // Altrimenti lo imposto alla lunghezza della lista
    inputField.setValue(listEditor.values.length);
  }, [inputField, listEditor.values]);

  return (
    <>
      {widget.kind === RepeatControllerComponentType.Input && (
        <ConfigFormItem
          name={options.name}
          label={<RichTextAdvancedRender inline text={options.label} />}
          help={<RichTextAdvancedRender inline text={options.helpText} />}
          layout={{ hideVisualLabel: options.hideLabel }}
          readonly={options.readonly}
        >
          {contextHolder}
          {/* Campo di input numerico custom */}
          <RepeatControllerInput
            name={options.name}
            placeholder={options.placeholder}
            value={options.value as any}
            tabIndex={options.preview ? -1 : undefined}
            applyChangeToList={handleChange}
            // Disabilito la rotellina del mouse per evitare che venga usata per cambiare il valore
            onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
              if ('blur' in e.target && typeof e.target.blur === 'function') {
                e.target.blur();
              }
            }}
            min={listEditor.itemRangeRuleValues.min}
            max={listEditor.itemRangeRuleValues.max}
          />
        </ConfigFormItem>
      )}
    </>
  );
}
